import PerfectScrollbar from 'perfect-scrollbar'
import React, {useRef} from 'react'
import ReactPerfectScrollbar from 'react-perfect-scrollbar'
import {Debouncer} from '@common/debouncer'
import {ScrollDirection} from './types'
import {ScrollProps} from './with-scrollbar'

const catchOffset = 100
const koef = 0.5

interface State {
  pressed: boolean
  y: number
  height: number
  x: number
  width: number
}

const initState: State = {
  pressed: false,
  y: 0,
  height: 0,
  x: 0,
  width: 0
}

export interface DragScrollProps extends ScrollProps {
  scrollDirection: ScrollDirection
  options: PerfectScrollbar.Options
}

const triggerDebouncer = new Debouncer(200)

export const DragPerfectScrollbar = (props: DragScrollProps) => {
  const {scrollDirection, options, ...otherProps} = props
  const ref = useRef<HTMLDivElement>()
  const refInterval = useRef<TimeoutHandler>()
  const refState = useRef<State>(initState)

  const on = () => {
    if (refState.current.pressed) {
      return
    }

    refState.current.pressed = true
    refInterval.current = setInterval(() => {
      updateScroll(scrollDirection, refState.current, ref.current)
    }, 20)
  }

  const off = () => {
    refState.current.pressed = false
    if (refInterval.current) {
      clearInterval(refInterval.current)
    }
  }

  const onDrag = (e: React.DragEvent) => {
    triggerDebouncer.runLatest(() => {
      off()
    })

    on()

    const el = ref.current
    if (!el) {
      return
    }

    const rect = el.getBoundingClientRect()

    if (scrollDirection === 'y' || scrollDirection === 'xy') {
      const offsetTop = rect.top
      refState.current.y = Math.max(e.clientY - offsetTop, 0)
      refState.current.height = el.clientHeight
    }

    if (scrollDirection === 'x' || scrollDirection === 'xy') {
      const offsetLeft = rect.left
      refState.current.x = Math.max(e.clientX - offsetLeft, 0)
      refState.current.width = el.clientWidth
    }
  }

  const setRef = (refEl: HTMLDivElement) => {
    ref.current = refEl
    otherProps.containerRef?.(refEl)
  }

  return (
    <ReactPerfectScrollbar
      onDragOver={onDrag}
      options={options}
      {...otherProps}
      containerRef={setRef}
    />
  )
}

const updateScroll = (
  scrollDirection: 'y' | 'x' | 'xy',
  state: State,
  el?: HTMLDivElement
) => {
  if (!el) {
    return
  }

  if (scrollDirection === 'y' || scrollDirection === 'xy') {
    const newScrollTop = getNewScrollValue(
      state.pressed,
      state.y,
      state.height,
      el.scrollTop,
      el.scrollHeight,
      catchOffset
    )

    if (newScrollTop !== undefined) {
      el.scrollTop = newScrollTop
    }
  }

  if (scrollDirection === 'x' || scrollDirection === 'xy') {
    const newScrollLeft = getNewScrollValue(
      state.pressed,
      state.x,
      state.width,
      el.scrollLeft,
      el.scrollWidth,
      catchOffset
    )
    if (newScrollLeft !== undefined) {
      el.scrollLeft = newScrollLeft
    }
  }
}

/**
 * Get new scroll position depending on the pressed and cursor position near the edge
 * @param pressed Is mouse pressed
 * @param position Current cursor position on 1D axis
 * @param size Size of the element on 1D axis
 * @param scrollTop Current scroll position
 * @param scrollHeight Total scroll height
 * @param catchOffset Offset from the edge to start scrolling
 */
const getNewScrollValue = (
  pressed: boolean,
  position: number,
  size: number,
  scrollTop: number,
  scrollHeight: number,
  catchOffset: number
): number | undefined => {
  const diff = size - position
  const isBottom = diff < catchOffset
  if (isBottom && pressed) {
    const rounded = Math.round((catchOffset - diff) / 10) * 10
    return Math.min(scrollTop + rounded * koef, scrollHeight)
  }

  const isTop = position < catchOffset
  if (isTop && pressed) {
    const rounded = Math.round((catchOffset - position) / 10) * 10
    return Math.max(scrollTop - rounded * koef, 0)
  }

  return undefined
}
